import React, { useState, useEffect } from 'react';
import { X, Mail, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MessagePopup({ onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/api/messages/unread`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(response.data.messages || []);
      } catch (error) {
        console.error('Failed to fetch unread messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUnreadMessages();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const handleClose = async () => {
    // Mark current message as read before closing
    if (messages[currentIndex]) {
      await markAsRead(messages[currentIndex].id);
    }
    
    // If there are more messages, show next one
    if (currentIndex < messages.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Dispatch event to update unread count in Navigation when all messages are read
      window.dispatchEvent(new CustomEvent('messageRead'));
      onClose();
    }
  };

  const handleCloseAndScan = async () => {
    // Mark current message as read
    if (messages[currentIndex]) {
      await markAsRead(messages[currentIndex].id);
    }
    
    // Dispatch event to update unread count in Navigation
    window.dispatchEvent(new CustomEvent('messageRead'));
    
    // Close popup and navigate to scanner page
    onClose();
    navigate('/scanner');
  };

  // No messages or loading
  if (loading || messages.length === 0) {
    return null;
  }

  const currentMessage = messages[currentIndex];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div 
        className="relative w-full max-w-2xl bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 120px)' }}
        data-testid="message-popup"
      >
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-gray-700/50">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors z-10"
            data-testid="close-message-popup"
            aria-label="Lukk melding"
          >
            <X size={24} />
          </button>

          {/* Mail icon and badge */}
          <div className="flex items-center gap-3 pr-12">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Ny melding fra styret</h2>
              {messages.length > 1 && (
                <p className="text-sm text-gray-400 mt-0.5">
                  Melding {currentIndex + 1} av {messages.length}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
          {/* Subject */}
          <h3 className="text-lg font-semibold text-white mb-2">
            {currentMessage.subject}
          </h3>

          {/* Sender and date */}
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
            <span>Fra: {currentMessage.sender_name}</span>
            <span>•</span>
            <span>
              {new Date(currentMessage.created_at).toLocaleDateString('nb-NO', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>

          {/* Message body */}
          <div 
            className="prose prose-invert max-w-none text-gray-200 leading-relaxed overflow-hidden"
            style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}
            dangerouslySetInnerHTML={{ __html: currentMessage.content }}
          />
        </div>

        {/* Footer with action button */}
        <div className="p-6 pt-4 border-t border-gray-700/50 bg-gray-900/50">
          <button
            onClick={handleCloseAndScan}
            className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-xl transition-colors text-lg"
            data-testid="close-and-scan-btn"
          >
            <QrCode size={24} />
            Lukk og skann QR
          </button>
          
          {messages.length > 1 && currentIndex < messages.length - 1 && (
            <button
              onClick={handleClose}
              className="w-full mt-3 py-3 px-6 text-gray-400 hover:text-white font-medium rounded-xl transition-colors"
              data-testid="next-message-btn"
            >
              Neste melding ({messages.length - currentIndex - 1} igjen)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
